import { spring } from 'motion';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { useIsMobile } from 'hooks/useIsMobile';
import { isTourMode, useTourMode } from 'tour/tourMode';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { linkPin, Pin, usePins } from 'utils/pins';

/** A Pinned row's height, and the step from one row to the next (`h-7`, `gap-px`), while no row is laid out. */
export const ROW_HEIGHT = 28;
export const ROW_PITCH = 29;
type Rows = { height: number; pitch: number };
const DRAWN_ROWS: Rows = { height: ROW_HEIGHT, pitch: ROW_PITCH };

/** Pinned's rows as drawn, which are 44px under a coarse pointer: a row's height and the step to the next. */
function measureRows(): Rows {
    const list = document.querySelector('[data-pin-list]');
    const [first, second] = [...(list?.children ?? [])] as HTMLElement[];
    if (!list || !first?.offsetHeight) return DRAWN_ROWS;
    const pitch = second
        ? second.offsetTop - first.offsetTop
        : first.offsetHeight + (parseFloat(getComputedStyle(list).rowGap) || 0);
    return { height: first.offsetHeight, pitch };
}
/** How far past Pinned's outline, or the rail's pin tile, the magnet pulls the chip in. */
export const REACH = 48;

/** The chip's offset from the pointer while it follows it. */
const CHIP_OFFSET = 14;
const TOAST_MS = 5000;
/**
 * How long each ending plays before the chip goes: shrinking into its row, the puff, the fade of a cancel. The
 * landing's is the chip's fade, which hides the tail of a glide still settling under it.
 */
export const ENDING_MS = { land: 180, puff: 200, cancel: 120 } as const;

/**
 * A spring with no bounce as CSS, WAAPI and dnd-kit take it: the `linear()` curve Motion writes for it, over its
 * settle time in ms, which is longer than the `visualDuration` it looks done at.
 */
function springTween(visualDuration: number) {
    const css = String(spring(visualDuration, 0));
    return { duration: parseFloat(css), easing: css.slice(css.indexOf(' ') + 1) };
}

/**
 * The glide onto the slot the magnet pulls into, Motion's `spring(0.18, 0)`: `GLIDE_SPRING` for a Motion component,
 * `GLIDE` for the rest. Home's grid moves its widgets, and a list its columns, on the same one.
 */
export const GLIDE_SPRING = { type: 'spring', visualDuration: 0.18, bounce: 0 } as const;
export const GLIDE = springTween(GLIDE_SPRING.visualDuration);
const GLIDE_IN = `transform ${GLIDE.duration}ms ${GLIDE.easing}`;
/** The glide back out of reach, a shorter move: `spring(0.12, 0)`. */
const GLIDE_BACK = springTween(0.12);
const FLASH_MS = 1200;

/**
 * A pin on its way into, within or out of Pinned. `from` is the dragged pin's place in Pinned, -1 for a record
 * link or a view; `index` the landing row's, among Pinned's rows without the dragged pin.
 */
export type PinDrag = {
    pin: Pin;
    from: number;
    /** Dragged in from outside while already pinned: nothing docks and nothing drops. */
    already: boolean;
    docked: boolean;
    index: number;
    /** Counts the dockings: the chip wiggles once for each. */
    docks: number;
    phase: 'drag' | keyof typeof ENDING_MS;
    /** Pinned's rows, measured as the drag starts. */
    rows: Rows;
};

type Snapshot = { drag: PinDrag | null; flash: string | null };

let snapshot: Snapshot = { drag: null, flash: null };
const listeners = new Set<() => void>();
const update = (next: Partial<Snapshot>) => {
    snapshot = { ...snapshot, ...next };
    listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/** The drag in progress, and the key of the pin just dropped while its row flashes. */
export const usePinDragState = (): Snapshot => useSyncExternalStore(subscribe, () => snapshot);

type Box = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;

/** Whether the pointer is within the magnet's reach of `box`. */
export const inReach = (x: number, y: number, box: Box): boolean =>
    x >= box.left - REACH && x <= box.right + REACH && y >= box.top - REACH && y <= box.bottom + REACH;

/**
 * The landing row the pointer's height picks: before the first row whose middle is below it, else the end. While the
 * chip is docked at `docked`, the rows from there on show one pitch lower and are measured where they show, so a pin
 * grabbed below its middle stays on its own row.
 */
export function landingIndex(y: number, listTop: number, rows: number, docked = -1, drawn = DRAWN_ROWS): number {
    for (let row = 0; row < rows; row += 1) {
        const slot = docked >= 0 && row >= docked ? row + 1 : row;
        if (listTop + slot * drawn.pitch + drawn.height / 2 > y) return row;
    }
    return rows;
}

/** How far the Pinned row at `position` slides off its place: up where the dragged pin left, down under the landing row. */
export function rowShift(position: number, drag: PinDrag): number {
    if (drag.phase !== 'drag' || position === drag.from) return 0;
    const after = drag.from >= 0 && position > drag.from;
    const place = after ? position - 1 : position;
    const { pitch } = drag.rows;
    return (after ? -pitch : 0) + (drag.docked && place >= drag.index ? pitch : 0);
}

// Created up front: Safari ignores a drag image that has not loaded yet.
const BLANK = new Image();
BLANK.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

let chip: HTMLElement | null = null;
let pointer = { x: 0, y: 0 };
let glideBackUntil = 0;
/** Whether this drag has placed the chip yet. */
let placed = false;
let reducedMotion = false;
let source: Element | null = null;
let startTimer: ReturnType<typeof setTimeout> | undefined;
let endTimer: ReturnType<typeof setTimeout> | undefined;
let flashTimer: ReturnType<typeof setTimeout> | undefined;

/** Where the docked chip sits: on the landing row, or on the rail's pin tile. */
function dockSlot(index: number, { height, pitch }: Rows) {
    const list = document.querySelector('[data-pin-list]')?.getBoundingClientRect();
    if (list) return { left: list.left, top: list.top + index * pitch, width: list.width };
    const tile = document.querySelector('[data-pin-tile]')?.getBoundingClientRect();
    // shortcut: the rail has no row to measure, so a coarse pointer's 44px chip sits 8px low on the tile; measure the
    // docked chip if that shows.
    return tile ? { left: tile.left, top: tile.top + (tile.height - height) / 2, width: undefined } : undefined;
}

/** Moves the chip with `transform` only, outside React: onto its slot while docked, else after the pointer. */
function placeChip() {
    const drag = snapshot.drag;
    if (!chip || !drag || drag.phase !== 'drag') return;
    const slot = drag.docked ? dockSlot(drag.index, drag.rows) : undefined;
    const gliding = slot
        ? GLIDE_IN
        : performance.now() < glideBackUntil
          ? `transform ${GLIDE_BACK.duration}ms ${GLIDE_BACK.easing}`
          : '';
    // The first placement jumps: a glide would start from where the chip was drawn before, 0,0 for a new one.
    chip.style.transition = reducedMotion || !placed ? '' : gliding;
    placed = true;
    chip.style.width = slot?.width ? `${slot.width}px` : '';
    const { x, y } = slot ? { x: slot.left, y: slot.top } : { x: pointer.x + CHIP_OFFSET, y: pointer.y + CHIP_OFFSET };
    chip.style.transform = `translate(${x}px, ${y}px)`;
}

/** The chip's element, which places itself as it mounts. */
export const setPinDragChip = (element: HTMLElement | null) => {
    chip = element;
    placeChip();
};

function end(phase: keyof typeof ENDING_MS) {
    clearTimeout(startTimer);
    source?.removeAttribute('data-pin-dragging');
    source = null;
    const drag = snapshot.drag;
    if (!drag || drag.phase !== 'drag') return;
    update({ drag: { ...drag, phase } });
    clearTimeout(endTimer);
    endTimer = setTimeout(() => update({ drag: null }), ENDING_MS[phase]);
}

function flash(key: string) {
    update({ flash: key });
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => update({ flash: null }), FLASH_MS);
}

const parsePin = (json: string | undefined): Pin | undefined => {
    try {
        return json ? (JSON.parse(json) as Pin) : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Pinning by dragging, for the new design's sidebar: a record link (`linkPin`) or a saved view (`data-pin`) dropped
 * on Pinned pins it there, a pin dragged within Pinned moves, and one let go out of its reach is unpinned. One
 * listener set on the document, off under 768px and during the guided tour. Reach and the landing row are geometry
 * read on each `dragover`, since a docked chip leaves the pointer free to roam outside Pinned.
 */
export function usePinDrag() {
    const isMobile = useIsMobile();
    const touring = useTourMode();
    const pinsApi = usePins();
    const latest = useRef(pinsApi);
    useEffect(() => {
        latest.current = pinsApi;
    });

    useEffect(() => {
        if (isMobile || touring) return undefined;

        const onDragStart = (event: DragEvent) => {
            const target = event.target;
            if (!(target instanceof Element) || !event.dataTransfer || isTourMode()) return;
            if (target.closest('[aria-modal="true"]')) return;
            const holder = target.closest<HTMLElement>('[data-pin]');
            const link = holder ? null : target.closest<HTMLAnchorElement>('a[href]');
            const pin = holder ? parsePin(holder.dataset.pin) : link ? linkPin(link) : undefined;
            if (!pin) return;

            const { pins } = latest.current;
            const from = holder?.closest('[data-pin-list]') ? pins.findIndex(({ key }) => key === pin.key) : -1;
            const { dataTransfer } = event;
            dataTransfer.effectAllowed = from >= 0 ? 'move' : 'copyLink';
            // A view's button carries no drag data of its own; a link keeps its own.
            if (dataTransfer.types.length === 0) dataTransfer.setData('text/plain', pin.label);
            dataTransfer.setDragImage(BLANK, 0, 0);

            clearTimeout(startTimer);
            clearTimeout(endTimer);
            source?.removeAttribute('data-pin-dragging');
            source = holder ?? link;
            source?.setAttribute('data-pin-dragging', '');
            reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            pointer = { x: event.clientX, y: event.clientY };
            glideBackUntil = 0;
            placed = false;
            const drag: PinDrag = {
                pin,
                from,
                already: from < 0 && pins.some(({ key }) => key === pin.key),
                // A pin lifts docked on its own row.
                docked: from >= 0,
                index: Math.max(from, 0),
                docks: from >= 0 ? 1 : 0,
                phase: 'drag',
                rows: measureRows(),
            };
            // Hiding the dragged pin's row, or scrolling it, while dragstart runs makes Chrome cancel the drag.
            startTimer = setTimeout(() => {
                update({ drag });
                // A chip still going from the last drag keeps its element, which no ref call places.
                placeChip();
                document.querySelector('[data-pin-zone]')?.scrollIntoView({ block: 'nearest' });
            });
        };

        const onDragOver = (event: DragEvent) => {
            const drag = snapshot.drag;
            if (!drag || drag.phase !== 'drag' || !event.dataTransfer) return;
            pointer = { x: event.clientX, y: event.clientY };
            const zone = document.querySelector('[data-pin-zone]') ?? document.querySelector('[data-pin-tile]');
            const reach = !!zone && inReach(pointer.x, pointer.y, zone.getBoundingClientRect());
            const docked = reach && !drag.already;
            const rows = latest.current.pins.length - (drag.from >= 0 ? 1 : 0);
            const list = document.querySelector('[data-pin-list]')?.getBoundingClientRect();
            const index = !docked
                ? drag.index
                : list
                  ? landingIndex(pointer.y, list.top, rows, drag.docked ? drag.index : -1, drag.rows)
                  : rows;
            if (docked !== drag.docked || index !== drag.index) {
                if (!docked) glideBackUntil = performance.now() + GLIDE_BACK.duration;
                update({
                    drag: { ...drag, docked, index, docks: docked && !drag.docked ? drag.docks + 1 : drag.docks },
                });
            }
            placeChip();
            // The document takes the drop only on Pinned, or anywhere for a pin, which a drop out of reach unpins.
            if (docked || drag.from >= 0) {
                event.preventDefault();
                event.dataTransfer.dropEffect = drag.from >= 0 ? 'move' : 'copy';
            } else if (reach) {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'none';
            }
        };

        const onDrop = (event: DragEvent) => {
            const drag = snapshot.drag;
            if (!drag || drag.phase !== 'drag' || (!drag.docked && drag.from < 0)) return;
            // Firefox would open a dropped link.
            event.preventDefault();
            const { pin, from, index } = drag;
            const { placePin, setPinned } = latest.current;
            if (drag.docked) {
                placePin(pin, index);
                end('land');
                if (from >= 0) return;
                flash(pin.key);
                successAlert(t('pins.pinned', { name: pin.label }), {
                    duration: TOAST_MS,
                    action: { label: t('pins.undo'), onClick: () => latest.current.setPinned(pin, false) },
                });
            } else {
                setPinned(pin, false);
                end('puff');
                successAlert(t('pins.unpinned', { name: pin.label }), {
                    duration: TOAST_MS,
                    action: { label: t('pins.undo'), onClick: () => latest.current.placePin(pin, from) },
                });
            }
        };

        // Esc, a drop out of reach, outside the window or elsewhere: nothing changes. A handled drop already ended.
        const onDragEnd = () => end('cancel');

        document.addEventListener('dragstart', onDragStart);
        document.addEventListener('dragover', onDragOver);
        document.addEventListener('drop', onDrop);
        document.addEventListener('dragend', onDragEnd);
        return () => {
            document.removeEventListener('dragstart', onDragStart);
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('drop', onDrop);
            document.removeEventListener('dragend', onDragEnd);
            onDragEnd();
        };
    }, [isMobile, touring]);
}
